import React, { useEffect, useState } from "react";
import { CSSTransition } from "react-transition-group";
import { Dict, EnumDict } from "@lana-commerce/core/commonTypes";
import { Portal } from "@lana-commerce/core/react/components/Portal";
import { RootCloseWrapper } from "@lana-commerce/core/react/components/RootCloseWrapper";
import { compact } from "@lana-commerce/core/compact";
import { generateUUID } from "@lana-commerce/core/generateUUID";
import { setLocale } from "@lana-commerce/core/translateLocale";
import { useStore } from "effector-react";

import { $cartCount, $currency, $customerInfo } from "../state/state";
import { CategoryBrief, CategoryRecursive, ShopData } from "../data/def";
import { Facebook } from "../components/icons/old/Facebook";
import { Footer } from "../components/Footer";
import { Header } from "../components/Header";
import { InfoLanguageFragment, MenuFragment, MenuItemFragment, ProductFragment } from "../graphql/types";
import { Instagram } from "../components/icons/old/Instagram";
import { LocaleSelector } from "../components/LocaleSelector";
import { MegaMenu, MegaSection } from "../components/MegaMenu";
import { MenuItem } from "../components/MenuItem";
import { Person } from "../components/icons/Person";
import { Pintrest } from "../components/icons/old/Pintrest";
import { PopupMenu } from "../components/PopupMenu";
import { Search } from "../components/icons/Search";
import { ShoppingBag } from "../components/icons/ShoppingBag";
import { T } from "./SiteLayout.tlocale";
import { TopBar } from "../components/TopBar";
import { Twitter } from "../components/icons/old/Twitter";
import { UI } from "../state/async/core";
import { UniversalLinkWrapper } from "../components/universalLink";
import { Youtube } from "../components/icons/old/Youtube";
import { extractCustomField, extractCustomFields } from "../utils/extractCustomFields";
import { findChildrenMenuItemsOf } from "../utils/findChildrenMenuItemsOf";
import { formatCurrencyLabel } from "../utils/formatCurrencyLabel";
import { getCategoryChain } from "../utils/getCategoryChain";
import { getMenuItemLink, getMenuItemLinkSpec } from "../utils/getMenuItemLink";
import { getMenuItems } from "../utils/profileMenuItems";
import { getProductCategories } from "../utils/useProductCategories";
import { onPageLoad } from "../utils/onPageLoad";
import { schemas } from "../cmodel/cmodel";
import { usePopperBasic, usePopperSameWidth } from "../utils/popper";
import { useShopData } from "../data/shopData";

function menuToMegaSections(categories: Dict<CategoryBrief>, menu: MenuFragment | null): MegaSection[] {
  const imageFilesMap = new Map(menu?.custom_field_objects?.files?.map((f) => [f?.id, f?.public_url]));
  const featured = extractCustomFields(schemas, "featured_category", menu?.custom_fields);
  const items = menu?.items;
  if (!items || items.length === 0) return [];
  const topLevel = findChildrenMenuItemsOf(items, -1);
  return (
    topLevel.map((v) => ({
      id: v.item.id,
      label: v.item.name,
      menus: findChildrenMenuItemsOf(items, v.index).map((v) => ({
        label: v.item.name,
        items: findChildrenMenuItemsOf(items, v.index).map((v) => ({
          label: v.item.name,
          to: getMenuItemLink(categories, v.item) || "/",
        })),
      })),
      featured: featured
        .filter((f) => f.location === v.item.category?.id)
        .map((f) => ({
          imgSrc: imageFilesMap.get(f.image) || "",
          title: f.title,
          subtitle: f.subtitle,
          to: categories[f.category]?.relativeUrl || "/",
        })),
    })) || []
  );
}

interface SiteHeaderProps {
  relativeUrl: string;
  topCategoryID: string | undefined;
  active: { section: string; menu: number; visible: boolean };
  setActive: React.Dispatch<
    React.SetStateAction<{
      section: string;
      menu: number;
      visible: boolean;
    }>
  >;
}

interface SiteProfileMenuProps {
  onClose: () => void;
}

const SiteProfileMenu = (props: SiteProfileMenuProps) => {
  const shopData = useShopData();
  const hasSavedCards = shopData.gateways.some((gw) => gw.type === "stripe" && !!gw.stripe?.card_tokenization);
  const menuItems = getMenuItems({
    goTo: (p) => {
      props.onClose();
      UI.showCustomerProfile({ action: "push", kind: p });
    },
    logout: () => {
      props.onClose();
      UI.logout();
    },
    hasSavedCards,
  });
  return (
    <PopupMenu>
      {menuItems.map((m) => (
        <MenuItem key={m.id} kind="button" onClick={m.onClick} label={m.label} />
      ))}
    </PopupMenu>
  );
};

const SiteHeader = (props: SiteHeaderProps) => {
  const shopData = useShopData();
  const uuid = useState(() => generateUUID())[0];
  const { active, setActive } = props;
  const { shop, megaMenu, categories } = shopData;
  const sections = menuToMegaSections(categories, megaMenu);
  const onMobileMenuClick = () => {
    setActive((a) => (a.visible ? { ...a, visible: false } : { section: sections[0]?.id, menu: -1, visible: true }));
  };
  const [profileVisible, setProfileVisible] = useState(false);
  const mmPopper = usePopperSameWidth("bottom");
  const pmenuPopper = usePopperBasic("bottom");
  const cartCount = useStore($cartCount);
  const customerInfo = useStore($customerInfo);
  const onPersonClick = () => {
    if (customerInfo) {
      setProfileVisible(true);
    } else {
      UI.showCustomerProfile();
    }
  };
  const onProfileClose = () => {
    setProfileVisible(false);
  };
  const matchingCategoryID = megaMenu?.items.find(
    (m) => m.parent_index === -1 && m.category?.id === props.topCategoryID
  )?.id;
  return (
    <>
      <Header
        ref={mmPopper.setReferenceElement}
        menuActive={active.visible ? active.section || matchingCategoryID : matchingCategoryID}
        menuItems={
          megaMenu?.items
            .filter((item) => item.parent_index === -1)
            .map((item) => ({
              id: item.id,
              label: () => item.name,
              onClick: () => {
                setActive({ section: item.id, menu: -1, visible: true });
              },
            })) || []
        }
        mobileMenu={{
          active: active.visible,
          rootCloseWrapperTag: uuid,
          onClick: onMobileMenuClick,
        }}
        title={shop.name}
        iconButtons={[
          { icon: Search, invisibleOnMobile: true, link: { kind: "link", to: "/search" } },
          {
            icon: Person,
            invisibleOnMobile: true,
            ref: pmenuPopper.setReferenceElement,
            link: {
              kind: "button",
              onClick: onPersonClick,
            },
          },
          {
            icon: ShoppingBag,
            count: cartCount || undefined,
            link: {
              kind: "button",
              onClick: () => {
                UI.showCart();
              },
            },
          },
        ]}
      />
      <Portal>
        <CSSTransition in={active.visible} timeout={200} mountOnEnter unmountOnExit>
          <div className="popup-menu" ref={mmPopper.setPopperElement} style={mmPopper.style} {...mmPopper.attrs}>
            <RootCloseWrapper tag={uuid} onRootClose={onMobileMenuClick}>
              <div className="bg-white w-[87.5%] tablet:w-full tablet:mt-[1px]">
                <MegaMenu
                  active={active}
                  onActiveChange={setActive}
                  sections={sections}
                  mobileMenuItems={[
                    <SiteLocaleSelector theme="light" relativeUrl={props.relativeUrl} />,
                    <MenuItem kind="link" to="/search" label={T("Search")} />,
                  ]}
                />
              </div>
            </RootCloseWrapper>
          </div>
        </CSSTransition>
      </Portal>
      <Portal>
        <CSSTransition in={profileVisible} timeout={200} mountOnEnter unmountOnExit>
          <RootCloseWrapper onRootClose={onProfileClose}>
            <div
              className="popup-menu"
              ref={pmenuPopper.setPopperElement}
              style={pmenuPopper.style}
              {...pmenuPopper.attrs}
            >
              <SiteProfileMenu onClose={onProfileClose} />
            </div>
          </RootCloseWrapper>
        </CSSTransition>
      </Portal>
    </>
  );
};

const formatLanguage = (lang: InfoLanguageFragment) => `${lang.name} [${lang.code}]`;

interface SiteLocaleSelectorProps {
  relativeUrl: string;
  theme?: "dark" | "light";
}

const SiteLocaleSelector = (props: SiteLocaleSelectorProps) => {
  const { locale, shop, currencies } = useShopData();
  const currency = useStore($currency) || shop.currency;
  const allCurrencies = compact(Object.values(currencies)).sort((a, b) =>
    a.currency.code.localeCompare(b.currency.code)
  );

  const allLangs = [
    ...(shop.language ? [{ label: formatLanguage(shop.language), value: shop.language.code }] : []),
    ...compact(shop.languages).map((l) => ({
      label: formatLanguage(l),
      value: l.code,
    })),
  ];
  return (
    <LocaleSelector
      theme={props.theme}
      languages={allLangs}
      language={locale}
      onLanguageChange={(v) => {
        window.location.href = `/${v}${props.relativeUrl}`;
      }}
      currencies={allCurrencies.map((c) => ({
        value: c.currency.code,
        label: formatCurrencyLabel(c),
      }))}
      currency={currency}
      onCurrencyChange={(v) => {
        UI.setCurrency(v);
      }}
    />
  );
};

interface SiteTopBarProps {
  relativeUrl: string;
}

const SiteTopBar = (props: SiteTopBarProps) => {
  const { categories, topBarMenu } = useShopData();
  const makeItem = (item: MenuItemFragment | undefined) =>
    item?.type === "empty"
      ? () => item.name
      : item
      ? () => <UniversalLinkWrapper spec={getMenuItemLinkSpec(categories, item)}>{item.name}</UniversalLinkWrapper>
      : undefined;
  const left = makeItem(topBarMenu?.items[0]);
  const center = makeItem(topBarMenu?.items[1]);
  return (
    <TopBar left={left} right={() => <SiteLocaleSelector relativeUrl={props.relativeUrl} />}>
      {center?.()}
    </TopBar>
  );
};

function emailFields(footerMenu: MenuFragment | null) {
  const subLink = extractCustomField(schemas, "email_sub_link", footerMenu?.custom_fields);
  const subForm = extractCustomField(schemas, "email_sub_form", footerMenu?.custom_fields);

  let onEmailSubmit = (_email: string) => {
    alert("Form action is not defined");
  };
  let emailForm: string | undefined = undefined;
  let emailTitle = T("Join our community");
  let emailIntro = T(
    "Sign up and get 10% off your first order! You'll also gain exclusive access to product releases + more."
  );
  if (subLink) {
    emailTitle = subLink.title;
    emailIntro = subLink.text;
    onEmailSubmit = (email: string) => {
      const link = subLink.link.replace("{email}", encodeURIComponent(email));
      window.open(link, "_blank", "noopener,noreferrer");
    };
  } else if (subForm) {
    emailTitle = subForm.title;
    emailIntro = subForm.text;
    emailForm = subForm.form;
  }
  return {
    onEmailSubmit,
    emailForm,
    emailTitle,
    emailIntro,
  };
}

const SiteFooter = () => {
  const shopData = useShopData();
  const { footerMenu, footerExtraMenu } = shopData;
  const [email, setEmail] = useState("");
  const socialIcons = extractCustomFields(schemas, "social_button", footerMenu?.custom_fields);
  const copyright = extractCustomField(schemas, "copyright", footerMenu?.custom_fields);
  const efields = emailFields(footerMenu);
  const iconsMap: EnumDict<(typeof socialIcons)[0]["kind"], (props: React.SVGProps<SVGSVGElement>) => JSX.Element> = {
    twitter: Twitter,
    facebook: Facebook,
    pinterest: Pintrest,
    instagram: Instagram,
    youtube: Youtube,
  };
  const footerMenuItems = footerMenu?.items || [];
  const footerExtraMenuItems = footerExtraMenu?.items || [];
  return (
    <Footer
      email={email}
      onEmailChange={setEmail}
      onEmailSubmit={() => {
        efields.onEmailSubmit(email);
      }}
      emailTitle={efields.emailTitle}
      emailIntro={efields.emailIntro}
      emailForm={efields.emailForm}
      copyright={copyright !== undefined ? copyright : T("Craftwork. All right reserved. © 2021")}
      links={findChildrenMenuItemsOf(footerExtraMenuItems, -1).map((menu) => {
        const item = menu.item;
        return {
          label: item.name,
          link: getMenuItemLinkSpec(shopData.categories, item),
        };
      })}
      socialLinks={socialIcons.map((v) => ({
        icon: iconsMap[v.kind],
        to: v.link,
      }))}
      menus={findChildrenMenuItemsOf(footerMenuItems, -1).map((menu) => {
        const children = findChildrenMenuItemsOf(footerMenuItems, menu.index);
        const item = menu.item;
        return {
          id: item.id,
          label: item.name,
          items: children.map((item) => ({
            label: item.item.name,
            link: getMenuItemLinkSpec(shopData.categories, item.item),
          })),
        };
      })}
    />
  );
};

function getTopLevelCategoryID(
  shopData: ShopData,
  product?: ProductFragment,
  category?: CategoryRecursive
): string | undefined {
  const cats = shopData.categories;
  if (category) {
    const chain = getCategoryChain(cats, category.id);
    return chain[0]?.id;
  } else if (product) {
    const chain = getProductCategories(shopData, product);
    return chain[0]?.id;
  }
  return undefined;
}

const AdditionalUI = () => {
  const [f, setF] = useState<{ render: () => JSX.Element } | null>(null);
  useEffect(() => {
    UI.afterLoad().then((m) => {
      setF({ render: m.state.renderAdditionalUI });
    });
  }, []);
  return f?.render() || null;
};

interface SiteLayoutProps {
  path?: string;
  children?: React.ReactNode;
  data?: {
    result?: {
      value?: {
        locale?: string;
        relativeUrl?: string;
        product?: ProductFragment;
        category?: CategoryRecursive;
      };
    };
  };
}

const SiteLayoutInner = (props: SiteLayoutProps) => {
  const shopData = useShopData();
  const [active, setActive] = useState({ section: "", menu: -1, visible: false });
  const value = props.data?.result?.value;
  const relativeUrl = value?.relativeUrl;
  const product = value?.product;
  const category = value?.category;
  const topCategoryID = getTopLevelCategoryID(shopData, product, category);

  useEffect(() => {
    onPageLoad(shopData);
    setActive((a) => ({ ...a, visible: false }));
  }, [props.path]);

  return (
    <div className="flex flex-col relative z-0 h-full">
      <div className="flex-shrink-0">
        <SiteTopBar relativeUrl={relativeUrl || "/"} />
      </div>
      <div className="flex-shrink-0 relative">
        <SiteHeader
          relativeUrl={relativeUrl || "/"}
          topCategoryID={topCategoryID}
          active={active}
          setActive={setActive}
        />
      </div>
      <div className="flex-grow relative -z-10">
        {props.children}
        <CSSTransition in={active.visible} timeout={200} mountOnEnter unmountOnExit>
          <div className="popup-menu absolute inset-0 bg-[rgba(112,114,118,0.7)]" />
        </CSSTransition>
      </div>
      <div className="flex-shrink-0">
        <SiteFooter />
      </div>
      <AdditionalUI />
    </div>
  );
};

export const SiteLayout = (props: SiteLayoutProps) => {
  const locale = props.data?.result?.value?.locale || "en";
  // Set locale before rendering and use it as "key" on a react fragment so that changed
  // locale forces re-render. T-string system uses a global variable, hence we have to rely
  // on this kind of side effect here.
  setLocale(locale);
  return (
    <React.Fragment key={locale}>
      <SiteLayoutInner {...props} />
    </React.Fragment>
  );
};
